import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";
import SEOMeta from "@/components/SEOMeta";
import LocalBusinessSchema from "@/components/LocalBusinessSchema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Home, Building, Shield, Wrench, MapPin, Phone, Mail, CheckCircle, Star, Users, Award, ExternalLink, AlertTriangle, Clock } from "lucide-react";

// Import roofing images
import roofingTeam from "@/assets/roofing-team.jpg";
import roofingInstallation from "@/assets/roofing-installation.jpg";
import roofRepairBeforeAfter from "@/assets/roof-repair-before-after.jpg";
import commercialRoofRepair from "@/assets/commercial-roof-repair.jpg";
import emergencyRoofRepair from "@/assets/emergency-roof-repair.jpg";

const RoofingServices = () => {
  const faqData = [
    {
      question: "Do you provide roofing services for apartment buildings in Niagara, Hamilton, and Toronto GTA?",
      answer: "Yes. Blue Knight Contracting specializes in commercial roofing for apartment buildings, condos, and multi-residential properties throughout Niagara Region, Hamilton, and the Greater Toronto Area."
    },
    {
      question: "Can you handle commercial roofing for plazas and warehouses in the GTA?",
      answer: "Absolutely. We provide expert commercial roofing solutions for retail plazas, shopping centers, warehouses, and industrial facilities across Toronto GTA, Hamilton, and Niagara with flat roofing and membrane systems."
    },
    {
      question: "What residential roofing services do you offer in Niagara Region?",
      answer: "We offer complete residential roofing services in Niagara including roof installation, replacement, repairs, inspections, and maintenance for all types of homes using premium materials and expert craftsmanship."
    },
    {
      question: "Do you provide emergency roof repair in Toronto, Hamilton, and Niagara?",
      answer: "Yes. Blue Knight Contracting provides 24/7 emergency roof repair services throughout Toronto GTA, Hamilton, and Niagara Region for storm damage, leaks, and urgent roofing issues on both residential and commercial properties."
    },
    {
      question: "What types of roofing materials do you work with?",
      answer: "We work with all roofing materials including asphalt shingles, metal roofing, flat membrane systems (TPO, EPDM, PVC), tile, slate, and cedar shake for residential homes, apartment buildings, plazas, and warehouses."
    },
    {
      question: "Do you provide roofing warranties in the GTA and Niagara?",
      answer: "Yes. We offer comprehensive warranties on both materials and workmanship for all our roofing projects in Toronto GTA, Hamilton, and Niagara Region, ensuring your investment is protected."
    }
  ];

  const serviceData = {
    "@type": "Service",
    name: "Commercial & Residential Roofing Services - Niagara, Hamilton, Toronto GTA",
    description: "Professional roofing services for apartment buildings, plazas, warehouses, and residential properties in Niagara Region, Hamilton, and Toronto GTA. Expert roof installation, repair, replacement, and emergency services.",
    provider: {
      "@type": "Organization",
      name: "Blue Knight Contracting and Construction",
      telephone: "[Insert Phone]",
      email: "[Insert Email]"
    },
    serviceType: "Commercial and Residential Roofing Services",
    areaServed: [
      {
        "@type": "City",
        name: "Toronto"
      },
      {
        "@type": "City", 
        name: "Hamilton"
      },
      {
        "@type": "City",
        name: "Niagara Falls"
      },
      {
        "@type": "City",
        name: "St. Catharines"
      },
      {
        "@type": "City",
        name: "Mississauga"
      },
      {
        "@type": "City",
        name: "Brampton"
      }
    ]
  };

  const roofingServices = [
    {
      title: "Commercial Apartment Building Roofing",
      description: "Expert roofing solutions for apartment buildings and multi-residential properties in Niagara, Hamilton, and Toronto GTA with flat roofing and membrane systems.",
      icon: Building
    },
    {
      title: "Plaza & Shopping Center Roofing",
      description: "Professional commercial roofing for retail plazas and shopping centers including waterproofing and flat roof installations across the GTA.",
      icon: Building
    },
    {
      title: "Warehouse & Industrial Roofing",
      description: "Heavy-duty roofing systems for warehouses and industrial facilities with durable materials designed for large-scale commercial applications.",
      icon: Building
    },
    {
      title: "Residential Roofing Services",
      description: "Complete residential roofing including installations, repairs, and replacements for homes throughout Niagara Region, Hamilton, and Toronto GTA.",
      icon: Home
    },
    {
      title: "Emergency Roof Services",
      description: "24/7 emergency roof repair services for storm damage, leaks, and urgent roofing issues on both commercial and residential properties.",
      icon: AlertTriangle
    },
    {
      title: "Roof Inspections & Maintenance",
      description: "Comprehensive roof inspections and preventive maintenance programs to extend roof lifespan and prevent costly repairs.",
      icon: CheckCircle
    }
  ];

  const serviceAreas = [
    "Greater Toronto Area",
    "Toronto", "Mississauga", "Brampton", "Vaughan", "Markham", "Richmond Hill", "Oakville", "Burlington", "Milton",
    "Niagara Region",
    "Niagara Falls", "St. Catharines", "Welland", "Niagara-on-the-Lake", "Fort Erie", "Grimsby", "Lincoln", "Pelham", "Port Colborne", "Thorold",
    "Eastern Ontario",
    "Ottawa", "Kingston", "Cornwall", "Belleville", "Pembroke", "Petawawa",
    "Southwestern Ontario", 
    "London", "Windsor", "Kitchener", "Waterloo", "Cambridge", "Guelph", "Sarnia", "Chatham",
    "Northern Ontario",
    "Sudbury", "Thunder Bay", "North Bay", "Sault Ste. Marie", "Timmins", "Kenora"
  ];

  return (
    <>
      <SEOMeta 
        title="Roofing Contractor — Repairs & Replacements | Niagara • Hamilton • GTA"
        description="Professional roofing for homes and commercial buildings in Niagara, Hamilton & the GTA. Shingle, flat and metal systems with leak repair."
        keywords="roofing contractor Niagara, Hamilton roof repair, GTA roofing company, shingles, flat roof, metal roofing, leak repair"
        canonicalUrl="https://blueknightcontracting.ca/services/roofing"
      />
      
      <LocalBusinessSchema
        locationName="Commercial & Residential Roofing Services - Niagara, Hamilton, Toronto GTA"
        addressLocality="Niagara Region"
        addressRegion="Ontario"
        description="Professional roofing services for apartment buildings, plazas, warehouses, and residential properties across Niagara Region, Hamilton, and the Greater Toronto Area. Expert commercial and residential roofing with 24/7 emergency service available."
        services={[
          "Apartment Building Roofing",
          "Plaza & Shopping Center Roofing",
          "Warehouse & Industrial Roofing",
          "Residential Roofing",
          "Commercial Flat Roofing",
          "Membrane Roofing Systems",
          "Emergency Roof Repair",
          "Roof Inspections",
          "Preventive Maintenance",
          "Shingle Roofing",
          "Metal Roofing"
        ]}
      />
      
      <SchemaMarkup 
        type="Service"
        data={{
          ...serviceData,
          mainEntity: {
            "@type": "FAQPage",
            mainEntity: faqData.map(faq => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer
              }
            }))
          }
        }}
      />
      
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary to-primary/80 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl lg:text-6xl font-bold mb-6">
                  Commercial & Residential Roofing Services in Niagara, Hamilton & Toronto GTA
                </h1>
                <p className="text-xl mb-8 opacity-90">
                  Blue Knight Contracting delivers expert roofing solutions for apartment buildings, plazas, warehouses, and residential properties throughout Niagara Region, Hamilton, and the Greater Toronto Area. From commercial flat roofing to residential installations, our licensed roofers provide quality workmanship with 24/7 emergency service.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Button asChild size="lg" variant="secondary">
                    <Link to="/contact">Get Free Quote</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="text-white bg-primary border-white hover:bg-white hover:text-primary">
                    <Link to="/services">View All Services</Link>
                  </Button>
                </div>
                
                {/* Blue Knight Roofing Link */}
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <Home className="h-6 w-6 text-white" />
                    <div>
                      <p className="font-semibold text-white">Specialized Roofing Division</p>
                      <a 
                        href="https://blueknightroofing.ca/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-white/90 hover:text-white underline underline-offset-4 flex items-center gap-1"
                      >
                        Visit Blue Knight Roofing <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={roofingInstallation} 
                  alt="Professional roofing team installing new shingles on Ontario residential roof"
                  className="rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Commercial & Residential Roofing Solutions
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Expert roofing for apartment buildings, plazas, warehouses, and homes across Niagara, Hamilton, and Toronto GTA with reliable solutions that protect your property.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {roofingServices.map((service, index) => (
                <Card key={index} className="h-full">
                  <CardHeader>
                    <service.icon className="h-12 w-12 text-primary mb-4" />
                    <CardTitle>{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Projects */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Featured Roofing Projects in Niagara, Hamilton & Toronto GTA
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Explore our portfolio of commercial and residential roofing projects across Niagara Region, Hamilton, and the Greater Toronto Area
              </p>
            </div>

            <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-8">
              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={roofingTeam} 
                    alt="Ontario roofing team installing shingles on new roof"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">New Installation</h3>
                  <p className="text-muted-foreground">
                    Complete roof installation with premium materials
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={roofRepairBeforeAfter} 
                    alt="Before and after roof repair showing damaged shingles replaced"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Roof Repairs</h3>
                  <p className="text-muted-foreground">
                    Professional repair and restoration services
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={commercialRoofRepair} 
                    alt="Commercial flat roof membrane installation and repair in Ontario"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Commercial Roofing</h3>
                  <p className="text-muted-foreground">
                    Flat roof and membrane system installations
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={emergencyRoofRepair} 
                    alt="Emergency roof repair during storm damage in Ontario"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Emergency Services</h3>
                  <p className="text-muted-foreground">
                    24/7 emergency repairs and storm damage response
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Emergency Services CTA */}
        <section className="py-16 bg-destructive text-white">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-4xl mx-auto">
              <AlertTriangle className="h-16 w-16 mx-auto mb-6" />
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Emergency Roof Repair Available 24/7
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Storm damage? Sudden leak? Don't wait - call our emergency roofing team now to prevent further damage to your property.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button size="lg" variant="secondary">
                  <Phone className="h-5 w-5 mr-2" />
                  Call Emergency Line
                </Button>
                <div className="flex items-center gap-2 text-white/90">
                  <Clock className="h-5 w-5" />
                  <span>Available 24/7</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Why Choose Blue Knight Roofing?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center p-6">
                <Users className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Certified Roofers</h3>
                <p className="text-muted-foreground">
                  Licensed and insured roofing professionals with years of experience
                </p>
              </Card>

              <Card className="text-center p-6">
                <Award className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Quality Materials</h3>
                <p className="text-muted-foreground">
                  Premium roofing materials from trusted manufacturers
                </p>
              </Card>

              <Card className="text-center p-6">
                <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Warranty Protection</h3>
                <p className="text-muted-foreground">
                  Comprehensive warranties on materials and workmanship
                </p>
              </Card>

              <Card className="text-center p-6">
                <Star className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Ontario Wide</h3>
                <p className="text-muted-foreground">
                  Serving communities across Ontario with reliable service
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Roofing Services Across Niagara, Hamilton & Toronto GTA
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Blue Knight Contracting provides professional commercial and residential roofing services throughout Niagara Region, Hamilton, and the Greater Toronto Area
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {serviceAreas.map((area, index) => {
                const isRegionHeader = area.includes("Ontario") || area.includes("Region") || area.includes("Area");
                const isNiagaraCity = ["Niagara Falls", "St. Catharines", "Welland", "Niagara-on-the-Lake", "Fort Erie", "Grimsby", "Lincoln", "Pelham", "Port Colborne", "Thorold"].includes(area);
                
                return (
                  <Card key={index} className={`p-4 text-center ${isRegionHeader ? 'bg-primary text-white col-span-full sm:col-span-2 lg:col-span-4' : ''}`}>
                    <MapPin className={`h-6 w-6 mx-auto mb-2 ${isRegionHeader ? 'text-white' : 'text-primary'}`} />
                    {isNiagaraCity ? (
                      <Link 
                        to={`/${area.toLowerCase().replace(/\s+/g, '-').replace(/'/g, '')}`} 
                        className="font-medium hover:text-primary underline underline-offset-4"
                      >
                        {area}
                      </Link>
                    ) : (
                      <p className={`font-medium ${isRegionHeader ? 'text-white text-lg' : ''}`}>{area}</p>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Common questions about our commercial and residential roofing services in Niagara, Hamilton, and Toronto GTA
              </p>
            </div>

            {/* YouTube Video Embed */}
            <div className="max-w-2xl mx-auto mb-12">
              <div className="aspect-video rounded-lg overflow-hidden shadow-lg">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/lb8ILPDqqiI"
                  title="Blue Knight Roofing Services"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            </div>

            <div className="max-w-4xl mx-auto space-y-8">
              {faqData.map((faq, index) => (
                <Card key={index} className="p-6">
                  <h3 className="text-xl font-semibold mb-3 text-primary">
                    {faq.question}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-primary text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready to Protect Your Property?
            </h2>
            <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
              Contact Blue Knight Contracting today for expert commercial and residential roofing services in Niagara Region, Hamilton, and Toronto GTA. Serving apartment buildings, plazas, warehouses, and homes. Get your free roof inspection and quote.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                <span>Call: [Insert Phone Number]</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                <span>Email: [Insert Email]</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                <span>Serving all of Ontario</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="secondary">
                <Link to="/contact">Get Free Roof Inspection</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-white bg-primary border-white hover:bg-white hover:text-primary">
                <a href="https://blueknightroofing.ca/" target="_blank" rel="noopener noreferrer">
                  Visit Blue Knight Roofing <ExternalLink className="h-4 w-4 ml-1" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default RoofingServices;